import React, { useEffect, useState, useMemo } from "react";
import useStore from "../../../useStore";
import Loader from "../../Elements/Loader/Loader";
import Button from "../../Elements/Button/Button";
import "./operadorInyeccion.css";
import Graficas from "./Graficas/Graficas";
import SearchInput from "../../Elements/Search/Search";

const OperadorInyeccion = () => {
  const { fetchOperadorInyeccion, operadorInyeccion, loading, error } = useStore();
  const [fecha, setFecha] = useState("");
  const [busquedaOp, setBusquedaOp] = useState("");
  const [viewMode, setViewMode] = useState("tabla");

  useEffect(() => {
    fetchOperadorInyeccion();
  }, [fetchOperadorInyeccion]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOperadorInyeccion(fecha);
  };

  const handleClear = () => {
    setFecha("");
    setBusquedaOp("");
    fetchOperadorInyeccion();
  };

  const getMonthName = (m) => {
    const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    return months[m - 1] || "";
  };

  // Cálculo del Top 3 mensual
  const monthlyTop3 = useMemo(() => {
    let yearF, monthF;
    if (fecha) {
      [yearF, monthF] = fecha.split("-");
    } else {
      const now = new Date();
      yearF = now.getFullYear().toString();
      monthF = String(now.getMonth() + 1).padStart(2, "0");
    }
    const rows = operadorInyeccion.filter(row => {
      const [y, m] = row.fecha.split("-");
      return y === yearF && m === monthF;
    });
    const sums = {};
    rows.forEach(row => {
      sums[row.operador] = (sums[row.operador] || 0) + Number(row.total_producido);
    });
    return Object.entries(sums)
      .map(([operador, total]) => ({ operador, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
  }, [fecha, operadorInyeccion]);

  // Filtrado por búsqueda y fecha
  const dataToShow = useMemo(() => {
    let base = operadorInyeccion;
    if (fecha) {
      const [yearF, monthF] = fecha.split("-");
      base = operadorInyeccion.filter(row => {
        const [y, m] = row.fecha.split("-");
        return y === yearF && m === monthF;
      });
    }
    const term = busquedaOp.toLowerCase().trim();
    if (!term) return base;
    return base.filter(row =>
      row.operador.toLowerCase().includes(term) ||
      row.turno.toLowerCase().includes(term) ||
      row.producto.toLowerCase().includes(term) ||
      row.inyectora.toLowerCase().includes(term)
    );
  }, [fecha, busquedaOp, operadorInyeccion]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const [year, month, day] = dateStr.split("-");
    return `${day} ${getMonthName(parseInt(month))} ${year}`;
  };

  const formatNumber = (num) =>
    isNaN(num) ? "-" : Number(num).toLocaleString("es-MX");

  return (
    <div className="oi-container py-4 container-mh">
      <h2 className="oi-header">Operador de Inyección</h2>

      {/* Formulario de filtros */}
      <form onSubmit={handleSubmit} className="oi-form row g-2 align-items-end">
        <div className="col-md-4">
          <label htmlFor="fecha" className="oi-form-label form-label">Mes</label>
          <input
            id="fecha"
            type="month"
            className="oi-input-month form-control"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
          />
        </div>
        <div className="col-md-2 oi-btn-group d-flex gap-2">
          <Button
            text="Borrar filtros"
            action={handleClear}
            disabled={false}
            color="red"
            fill
            padding={false}
            classNames="w-100"
          />
        </div>
      </form>

      {/* Buscador */}
      <div className="oi-search mb-4">
        <SearchInput
          value={busquedaOp}
          onChange={setBusquedaOp}
          onClear={() => setBusquedaOp("")}
          placeholder="Buscar por operador, turno, producto, inyectora"
          showClear={false}
        />
      </div>

      {/* Toggle de vista */}
      <div className="oi-view-toggle mb-4 d-flex gap-2">
        <Button
          text="Tabla"
          action={() => setViewMode("tabla")}
          color={viewMode === "tabla" ? "blue" : "gray"}
          fill
        />
        <Button
          text="Gráficas"
          action={() => setViewMode("graficas")}
          color={viewMode === "graficas" ? "blue" : "gray"}
          fill
        />
      </div>

      {/* Loader / Error */}
      {loading && <Loader />}
      {!loading && error && (
        <div className="oi-alert alert alert-danger">Error: {error}</div>
      )}

      {/* Vista de tabla */}
      {!loading && !error && viewMode === "tabla" && (
        <>
          {/* Top 3 mensual */}
          {monthlyTop3.length > 0 && (
            <div className="oi-top3 mb-4">
              <h5 className="oi-top3-title">
                Top 3 {fecha
                  ? `de ${getMonthName(parseInt(fecha.split("-")[1]))} ${fecha.split("-")[0]}`
                  : `del mes actual`}
              </h5>
              <ol className="oi-top3-list">
                {monthlyTop3.map(({ operador, total }, i) => (
                  <li key={i}>
                    {operador}: {total.toLocaleString("es-MX")} unidades
                    <span className="oi-top-badge">#{i + 1}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Tabla de datos */}
          <div className="oi-table-wrapper table-responsive">
            <table className="oi-table table table-striped">
              <thead className="table-light">
                <tr>
                  <th>Operador</th>
                  <th>Turno</th>
                  <th>Producto</th>
                  <th>Descripción</th>
                  <th>Inyectora</th>
                  <th className="text-end">Total Producido</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {dataToShow.map((row, idx) => (
                  <tr key={idx}>
                    <td data-label="Operador">{row.operador}</td>
                    <td data-label="Turno">{row.turno}</td>
                    <td data-label="Producto">{row.producto}</td>
                    <td data-label="Descripción">{row.descripcion}</td>
                    <td data-label="Inyectora">{row.inyectora}</td>
                    <td data-label="Total Producido" className="text-end">
                      {formatNumber(row.total_producido)}
                    </td>
                    <td data-label="Fecha">{formatDate(row.fecha)}</td>
                  </tr>
                ))}
              </tbody>

            </table>
            {dataToShow.length === 0 && (
              <p className="oi-no-data text-muted">
                No hay datos de inyección para la búsqueda actual.
              </p>
            )}
          </div>
        </>
      )}

      {/* Vista de gráficas */}
      {!loading && !error && viewMode === "graficas" && (
        <Graficas data={dataToShow} busquedaOp={busquedaOp} />
      )}
    </div>
  );
};

export default OperadorInyeccion;